import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { DebtResponse, DebtType, clientsApi } from "@/api";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export interface DebtRowValues {
  id?: string;
  value: string;
  isInput?: boolean;
}

export interface MeasuresCausesFormValues {
  measures: DebtRowValues[];
  causes: DebtRowValues[];
}

const EMPTY_ROW: DebtRowValues = { value: "", isInput: true };

function buildInitialRows(saved: DebtResponse[], type: DebtType): DebtRowValues[] {
  const savedRows = saved.filter((d) => d.type === type).map((d) => ({ id: d.id, value: d.value }));
  return savedRows.length > 0 ? savedRows : [EMPTY_ROW];
}

export function useMeasuresCauses(clientId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const { data: savedDebts = [] } = useQuery<DebtResponse[]>({
    queryKey: [QueryKey.ClientDebts, clientId],
    queryFn: () => clientsApi.getMeasuresCausesByClientId({ clientId }),
    meta: { loadingScope: "pageContent" },
  });

  const { clientMeasures: measureOptions, clientCauses: causeOptions } = useDropdownOptions();

  const syncMutation = useMutation({
    mutationFn: (values: MeasuresCausesFormValues) => {
      const measures = values.measures
        .filter((r) => r.value !== "")
        .reverse()
        .map((r) => ({ id: r.id ?? null, type: DebtType.Measure, value: r.value }));
      const causes = values.causes
        .filter((r) => r.value !== "")
        .reverse()
        .map((r) => ({ id: r.id ?? null, type: DebtType.Cause, value: r.value }));
      return clientsApi.updateClientMeasuresCauses({
        clientId,
        updateClientDebtsRequest: { debts: [...measures, ...causes] },
      });
    },
  });

  const save = async (values: MeasuresCausesFormValues) => {
    await syncMutation.mutateAsync(values);
    showSuccess(t("clients.measuresCauses.saveSuccess"));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientDebts, clientId] });
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        measures: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().when("isInput", {
              is: (isInput: boolean | undefined) => !isInput,
              then: (schema) => schema.required(t("clients.measuresCauses.measureRequired")),
            }),
          })
        ),
        causes: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().when("isInput", {
              is: (isInput: boolean | undefined) => !isInput,
              then: (schema) => schema.required(t("clients.measuresCauses.causeRequired")),
            }),
          })
        ),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<MeasuresCausesFormValues>({
    initialValues: {
      measures: buildInitialRows(savedDebts, DebtType.Measure),
      causes: buildInitialRows(savedDebts, DebtType.Cause),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await save(values);
    },
  });

  const handleAddMeasure = () => {
    void formik.setFieldValue("measures" satisfies keyof MeasuresCausesFormValues, [
      EMPTY_ROW,
      ...formik.values.measures,
    ]);
  };

  const handleDeleteMeasure = (index: number) => {
    void formik.setFieldValue(
      "measures" satisfies keyof MeasuresCausesFormValues,
      formik.values.measures.filter((_, i) => i !== index)
    );
  };

  const handleAddCause = () => {
    void formik.setFieldValue("causes" satisfies keyof MeasuresCausesFormValues, [EMPTY_ROW, ...formik.values.causes]);
  };

  const handleDeleteCause = (index: number) => {
    void formik.setFieldValue(
      "causes" satisfies keyof MeasuresCausesFormValues,
      formik.values.causes.filter((_, i) => i !== index)
    );
  };

  return {
    formik,
    measureOptions,
    causeOptions,
    handleAddMeasure,
    handleDeleteMeasure,
    handleAddCause,
    handleDeleteCause,
  };
}
